package io.github.retar.portfolio.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.browser.util.invokeLater
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.pointerEvents
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.thenIfNotNull
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.common.SmoothColorStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.name
import io.github.retar.portfolio.styles.sitePalette
import io.github.retar.portfolio.styles.toSitePalette
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Text

val CodeBlockStyle = CssStyle.base(extraModifier = { SmoothColorStyle.toModifier() }) {
    val palette = colorMode.toSitePalette()
    Modifier
        .backgroundColor(palette.codeBackground)
        .color(palette.codeText)
        .borderRadius(6.px)
        .maxWidth(100.percent)
        .overflow { x(Overflow.Auto) }
        .whiteSpace(WhiteSpace.Pre)
        .padding(1.em)
}

val CodeLabelStyle = CssStyle.base {
    Modifier
        .position(Position.Absolute)
        .color("var(--mono-2)".unsafeCast<CSSColorValue>())
        .fontSize(0.75.em)
        .top((-1.75).em)
        .left(0.em)
        .borderRadius(5.px)
        .pointerEvents(PointerEvents.None)
        .styleModifier { property("font-family", "'Geist Mono', ui-monospace, monospace") }
}

/**
 * Creates a code block that is colored by PrismJs
 */
// Note: To enable this widget to work, we needed to add PrismJs support to this project. See the kobweb
// block in our build.gradle.kts file to see how this was done.
@Composable
fun CodeBlock(
    code: String,
    modifier: Modifier = Modifier,
    lang: String? = null,
    highlightLines: String? = null,
    label: String? = null
) {
    Pre(
        CodeBlockStyle.toModifier()
            .thenIfNotNull(highlightLines) { Modifier.attr("data-line", it) }
            .thenIfNotNull(label) {
                Modifier
                    .margin { top(2.em) }
                    .attrsModifier {
                        ref { preElement ->
                            window.invokeLater { // Give prismjs plugin a frame to create the parent toolbar
                                preElement.parentElement?.takeIf { it.classList.contains("code-toolbar") }
                                    ?.let { toolbar ->
                                        preElement.insertAdjacentElement(
                                            "afterend",
                                            document.createElement("div").apply {
                                                textContent = label
                                                className = CodeLabelStyle.name
                                            })
                                    }
                            }
                            onDispose { }
                        }
                    }
            }
            .toAttrs()) {
        val palette = sitePalette()
        Code(
            attrs = SmoothColorStyle.toModifier()
                .display(DisplayStyle.Block)
                .color(palette.codeText)
                // Ensure iOS doesn't shrink text size when zooming
                .styleModifier { property("-webkit-text-size-adjust", 100.percent) }
                .classNames("language-${lang ?: "none"}")
                .thenIf(lang?.startsWith("diff") == true, Modifier.classNames("diff-highlight"))
                .then(modifier)
                .toAttrs()
        ) {
            Text(code)
        }
    }
}
